import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Save, Search, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/core/auth/auth";

/**
 * `normal_assertion_library`, `negative_assertion_library` and
 * `user_template_profiles` are not in the generated Supabase types yet, so the
 * typed client refuses them. This is the single named escape hatch for that gap:
 * grep `untypedSupabase` to find every call that depends on it, and delete it
 * once the types are regenerated from the sandbox.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- see above; removed when Supabase types are regenerated
const untypedSupabase = supabase as any;

type AssertionStyle = "negative" | "normal" | "unremarkable";
type OtherBehavior = "dynamic" | "persistent";

interface ExamFamilyOption {
  exam_family_id: string;
  display_name: string;
  modality: string;
  body_region: string | null;
  contrast_phase: string | null;
}

type XrLaterality = "LEFT" | "RIGHT" | "BILATERAL" | "NONE";

interface BaseSection {
  section_token: string;
  section_title: string;
  order_index: number;
}

interface TemplateHeaderConfig {
  exam_label: string;
  technique: string;
  comparison: string;
  indication: string;
  standard_indication: string | null;
  field_order: string[];
  include_date: boolean;
  include_contrast: boolean;
  include_age: boolean;
  include_dob: boolean;
  extra_header_lines: string[];
  xr_default_views: number | null;
}

interface StructureAssertionSelection {
  structure_key: string;
  style: AssertionStyle;
  text: string;
}

interface TemplateProfileConfig {
  section_order: string[];
  hidden_sections: string[];
  assertion_styles: Record<string, AssertionStyle>;
  phrase_variants: Record<string, string>;
  custom_assertions: Record<string, string>;
  section_structure_assertions: Record<string, StructureAssertionSelection[]>;
  other_mode: OtherBehavior;
  header: TemplateHeaderConfig;
}

interface TemplateProfile {
  id: string;
  profile_name: string;
  is_default: boolean;
  status: "draft" | "published";
  config: TemplateProfileConfig;
}

interface AssertionRow {
  structure_key: string;
  modality: string | null;
  normal_text?: string | null;
  negative_text?: string | null;
}

const STYLE_OPTIONS: AssertionStyle[] = ["negative", "normal", "unremarkable"];

const PHRASE_OPTIONS_BY_STYLE: Record<AssertionStyle, string[]> = {
  negative: [
    "No acute abnormality.",
    "No focal abnormality.",
    "No suspicious finding.",
  ],
  normal: [
    "Normal appearance.",
    "Within normal limits.",
    "No abnormality detected.",
  ],
  unremarkable: [
    "Unremarkable.",
    "No significant abnormality.",
    "No notable finding.",
  ],
};

const SECTION_RELEVANT_PHRASES: Array<{
  test: RegExp;
  options: Record<AssertionStyle, string[]>;
}> = [
  {
    test: /(brain|parenchyma|cerebral|cerebell)/i,
    options: {
      normal: ["Gray-white differentiation is preserved.", "No focal parenchymal signal abnormality."],
      negative: ["No large territorial ischemic infarct.", "No acute intraparenchymal hemorrhage."],
      unremarkable: ["Brain parenchyma is unremarkable."],
    },
  },
  {
    test: /(ventricle|csf|cistern)/i,
    options: {
      normal: ["Ventricular size and configuration are within normal limits."],
      negative: ["No hydrocephalus."],
      unremarkable: ["Ventricular system is unremarkable."],
    },
  },
  {
    test: /(lung|airway|pulmonary)/i,
    options: {
      normal: ["Lungs are clear.", "Central airways are patent."],
      negative: ["No focal airspace consolidation.", "No pleural effusion or pneumothorax."],
      unremarkable: ["Lung parenchyma is unremarkable."],
    },
  },
  {
    test: /(heart|cardiac|mediast)/i,
    options: {
      normal: ["Cardiomediastinal silhouette is within normal limits."],
      negative: ["No cardiomegaly.", "No pericardial effusion."],
      unremarkable: ["Cardiomediastinal structures are unremarkable."],
    },
  },
  {
    test: /(liver|hepatic)/i,
    options: {
      normal: ["Liver demonstrates normal morphology."],
      negative: ["No focal hepatic lesion."],
      unremarkable: ["Liver is unremarkable."],
    },
  },
  {
    test: /(bone|osseous|marrow|spine|calvarium)/i,
    options: {
      normal: ["Normal marrow signal.", "No acute osseous abnormality."],
      negative: ["No acute fracture.", "No aggressive osseous lesion."],
      unremarkable: ["Osseous structures are unremarkable."],
    },
  },
];

function buildDefaultHeader(exam: ExamFamilyOption | null): TemplateHeaderConfig {
  return {
    exam_label: exam ? canonicalExamLabel(exam, exam.modality === "XR" ? 2 : null) : "",
    technique: "Not specified.",
    comparison: "None available.",
    indication: "Not specified.",
    standard_indication: null,
    field_order: ["EXAM", "TECHNIQUE", "COMPARISON", "INDICATION"],
    include_date: false,
    include_contrast: false,
    include_age: false,
    include_dob: false,
    extra_header_lines: [],
    xr_default_views: exam?.modality === "XR" ? 2 : null,
  };
}

function prettifyAnatomy(input: string): string {
  return input
    .replace(/\b(xr|xray|x-ray|radiograph|radiography)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function detectLaterality(input: string): XrLaterality {
  const s = input.toLowerCase();
  if (/\b(bilateral|both)\b/.test(s)) return "BILATERAL";
  if (/\b(left|lt)\b/.test(s)) return "LEFT";
  if (/\b(right|rt)\b/.test(s)) return "RIGHT";
  return "NONE";
}

function stripLaterality(input: string): string {
  return input.replace(/\b(left|right|bilateral|both|lt|rt)\b/gi, " ").replace(/\s+/g, " ").trim();
}

function extractAnatomy(exam: ExamFamilyOption): string {
  if (exam.body_region?.trim()) return prettifyAnatomy(exam.body_region);
  return prettifyAnatomy(exam.display_name) || "ANATOMY";
}

function extractXrAnatomyWithoutLaterality(exam: ExamFamilyOption): string {
  return stripLaterality(extractAnatomy(exam));
}

function extractXrSubject(exam: ExamFamilyOption | null): string {
  if (!exam) return "the x-ray region";
  const anatomy = extractAnatomy(exam);
  return `the ${anatomy.toLowerCase()}`;
}

function canonicalExamLabel(exam: ExamFamilyOption, xrViews: number | null): string {
  if (exam.modality !== "XR") return exam.display_name;
  const anatomy = extractAnatomy(exam).toUpperCase();
  const views = Math.max(1, Number(xrViews || 2));
  return `XR ${anatomy} ${views}V`;
}

function xrTechniqueWithAnatomy(exam: ExamFamilyOption | null, viewCount: number): string {
  const subject = extractXrSubject(exam);
  return `${viewCount} view${viewCount === 1 ? "" : "s"} of ${subject} obtained.`;
}

function standardIndicationOptions(exam: ExamFamilyOption | null): string[] {
  if (!exam) return [];
  const anatomy = extractAnatomy(exam);
  if (exam.modality === "XR") {
    return [
      `${anatomy} pain.`,
      `Trauma, ${anatomy}.`,
      `Follow-up ${anatomy} findings.`,
    ];
  }
  if (exam.modality === "CT") {
    return [
      `${anatomy} pain.`,
      `Evaluate for acute ${anatomy.toLowerCase()} pathology.`,
      `Abnormal prior imaging follow-up.`,
    ];
  }
  if (exam.modality === "MRI") {
    return [
      `Persistent ${anatomy.toLowerCase()} symptoms.`,
      `Neurologic deficit evaluation.`,
      `Preoperative planning.`,
    ];
  }
  return [`Clinical concern involving ${anatomy.toLowerCase()}.`];
}

function defaultConfig(sections: BaseSection[], exam: ExamFamilyOption | null): TemplateProfileConfig {
  return {
    section_order: sections.map((s) => s.section_token),
    hidden_sections: [],
    assertion_styles: Object.fromEntries(sections.map((s) => [s.section_token, "negative"])) as Record<string, AssertionStyle>,
    phrase_variants: Object.fromEntries(sections.map((s) => [s.section_token, PHRASE_OPTIONS_BY_STYLE.negative[0]])),
    custom_assertions: {},
    section_structure_assertions: {},
    other_mode: "dynamic",
    header: buildDefaultHeader(exam),
  };
}

function isOtherToken(sectionToken: string): boolean {
  const t = sectionToken.toUpperCase();
  return t === "OTHER" || t === "OTHER_FINDINGS";
}

function normalize(v: string): string {
  return v.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
}

function fuzzyMatch(query: string, target: string): boolean {
  const q = normalize(query);
  if (!q) return true;
  const t = normalize(target);
  const parts = q.split(" ").filter(Boolean);
  let cursor = 0;
  for (const part of parts) {
    const idx = t.indexOf(part, cursor);
    if (idx === -1) return false;
    cursor = idx + part.length;
  }
  return true;
}

function stylePreview(section: string, style: AssertionStyle, phrase: string): string {
  if (phrase) return phrase;
  if (style === "normal") return `${section} is normal.`;
  if (style === "unremarkable") return "Unremarkable.";
  return `No acute ${section.toLowerCase()} abnormality.`;
}

function getSectionRelevantPhraseOptions(
  sectionToken: string,
  sectionTitle: string,
  style: AssertionStyle,
  dynamicOptionsBySection: Record<string, Record<AssertionStyle, string[]>>
): string[] {
  const fromData = dynamicOptionsBySection[sectionToken]?.[style] ?? [];
  if (fromData.length > 0) return fromData;
  const source = `${sectionToken} ${sectionTitle}`;
  for (const rule of SECTION_RELEVANT_PHRASES) {
    if (rule.test.test(source)) {
      return rule.options[style];
    }
  }
  return PHRASE_OPTIONS_BY_STYLE[style];
}

function hydrateConfig(raw: Partial<TemplateProfileConfig> | null | undefined, sections: BaseSection[], exam: ExamFamilyOption | null): TemplateProfileConfig {
  const defaults = defaultConfig(sections, exam);
  const sectionTokens = sections.map((s) => s.section_token);

  const sectionOrder = Array.from(
    new Set([...(raw?.section_order ?? []).filter((t) => sectionTokens.includes(t)), ...sectionTokens])
  );
  const hiddenSections = Array.from(
    new Set((raw?.hidden_sections ?? []).filter((t) => sectionTokens.includes(t)))
  );

  const assertionStyles = {
    ...defaults.assertion_styles,
    ...(raw?.assertion_styles ?? {}),
  };

  const phraseVariants = { ...defaults.phrase_variants, ...(raw?.phrase_variants ?? {}) };
  const customAssertions = { ...(raw?.custom_assertions ?? {}) };
  const sectionStructureAssertions = { ...(raw?.section_structure_assertions ?? {}) };

  const hdr = raw?.header ?? defaults.header;
  const header: TemplateHeaderConfig = {
    exam_label: hdr.exam_label?.trim() || defaults.header.exam_label,
    technique: hdr.technique?.trim() || defaults.header.technique,
    comparison: hdr.comparison?.trim() || defaults.header.comparison,
    indication: hdr.indication?.trim() || defaults.header.indication,
    standard_indication:
      typeof hdr.standard_indication === "string" && hdr.standard_indication.trim().length > 0
        ? hdr.standard_indication
        : null,
    field_order: Array.isArray(hdr.field_order)
      ? hdr.field_order.map((x) => String(x)).filter(Boolean)
      : defaults.header.field_order,
    include_date: Boolean((hdr as Partial<TemplateHeaderConfig>).include_date),
    include_contrast: Boolean((hdr as Partial<TemplateHeaderConfig>).include_contrast),
    include_age: Boolean((hdr as Partial<TemplateHeaderConfig>).include_age),
    include_dob: Boolean((hdr as Partial<TemplateHeaderConfig>).include_dob),
    extra_header_lines: Array.isArray(hdr.extra_header_lines)
      ? hdr.extra_header_lines.map((l) => String(l)).filter(Boolean)
      : [],
    xr_default_views:
      typeof hdr.xr_default_views === "number" && Number.isFinite(hdr.xr_default_views)
        ? Math.max(1, Math.min(8, Math.round(hdr.xr_default_views)))
        : defaults.header.xr_default_views,
  };

  const otherMode: OtherBehavior = raw?.other_mode === "persistent" ? "persistent" : "dynamic";

  return {
    section_order: sectionOrder,
    hidden_sections: hiddenSections,
    assertion_styles: assertionStyles,
    phrase_variants: phraseVariants,
    custom_assertions: customAssertions,
    section_structure_assertions: sectionStructureAssertions,
    other_mode: otherMode,
    header,
  };
}

export function PortalTemplateBuilder() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [examFamilies, setExamFamilies] = useState<ExamFamilyOption[]>([]);
  const [examFamilyId, setExamFamilyId] = useState<string>("");
  const [examSearch, setExamSearch] = useState("");
  const [modalityFilter, setModalityFilter] = useState("ALL");
  const [contrastFilter, setContrastFilter] = useState("ALL");
  const [bodyRegionFilter, setBodyRegionFilter] = useState("ALL");
  const [xrLateralityFilter, setXrLateralityFilter] = useState<"ALL" | XrLaterality>("ALL");

  const [sections, setSections] = useState<BaseSection[]>([]);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [profileName, setProfileName] = useState("Default");
  const [isDefault, setIsDefault] = useState(true);
  const [allowAdminCloudSave, setAllowAdminCloudSave] = useState(false);
  const [sectionStructureSearch, setSectionStructureSearch] = useState<Record<string, string>>({});
  const [sectionStructureMap, setSectionStructureMap] = useState<Record<string, string[]>>({});
  const [assertionOptionsBySection, setAssertionOptionsBySection] = useState<Record<string, Record<AssertionStyle, string[]>>>({});
  const [normalOptionsByStructure, setNormalOptionsByStructure] = useState<Record<string, string[]>>({});
  const [negativeOptionsByStructure, setNegativeOptionsByStructure] = useState<Record<string, string[]>>({});
  const [pendingStructureAdd, setPendingStructureAdd] = useState<Record<string, string>>({});
  const [config, setConfig] = useState<TemplateProfileConfig>(defaultConfig([], null));
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const selectedExam = useMemo(
    () => examFamilies.find((e) => e.exam_family_id === examFamilyId) ?? null,
    [examFamilies, examFamilyId]
  );

  const orderedSections = useMemo(() => {
    const byToken = new Map(sections.map((s) => [s.section_token, s]));
    const explicit = config.section_order.map((token) => byToken.get(token)).filter(Boolean) as BaseSection[];
    const missing = sections.filter((s) => !config.section_order.includes(s.section_token));
    return [...explicit, ...missing].filter((s) => !config.hidden_sections.includes(s.section_token));
  }, [sections, config.section_order, config.hidden_sections]);
  const removedSections = useMemo(
    () => sections.filter((s) => config.hidden_sections.includes(s.section_token)),
    [sections, config.hidden_sections]
  );

  const modalityOptions = useMemo(
    () => ["ALL", ...Array.from(new Set(examFamilies.map((e) => e.modality))).sort()],
    [examFamilies]
  );
  const contrastOptions = useMemo(
    () => ["ALL", ...Array.from(new Set(examFamilies.map((e) => e.contrast_phase || "UNSPECIFIED"))).sort()],
    [examFamilies]
  );
  const bodyRegionOptions = useMemo(
    () => ["ALL", ...Array.from(new Set(examFamilies.map((e) => e.body_region || "UNSPECIFIED"))).sort()],
    [examFamilies]
  );

  const filteredExamFamilies = useMemo(() => {
    return examFamilies.filter((exam) => {
      if (modalityFilter !== "ALL" && exam.modality !== modalityFilter) return false;
      const examContrast = exam.contrast_phase || "UNSPECIFIED";
      if (contrastFilter !== "ALL" && examContrast !== contrastFilter) return false;
      const examRegion = exam.body_region || "UNSPECIFIED";
      if (bodyRegionFilter !== "ALL" && examRegion !== bodyRegionFilter) return false;
      if (modalityFilter === "XR" && xrLateralityFilter !== "ALL") {
        const lat = detectLaterality(`${exam.display_name} ${exam.body_region ?? ""} ${exam.exam_family_id}`);
        if (lat !== xrLateralityFilter) return false;
      }

      if (!examSearch.trim()) return true;
      return fuzzyMatch(
        examSearch,
        [exam.display_name, exam.exam_family_id, exam.modality, exam.body_region || "", exam.contrast_phase || ""].join(" ")
      );
    });
  }, [examFamilies, modalityFilter, contrastFilter, bodyRegionFilter, xrLateralityFilter, examSearch]);

  const groupedXrExamFamilies = useMemo(() => {
    const groups = new Map<string, {
      anatomyLabel: string;
      LEFT: ExamFamilyOption | null;
      RIGHT: ExamFamilyOption | null;
      BILATERAL: ExamFamilyOption | null;
      NONE: ExamFamilyOption | null;
    }>();
    for (const exam of filteredExamFamilies) {
      if (exam.modality !== "XR") continue;
      const key = normalize(extractXrAnatomyWithoutLaterality(exam));
      if (!key) continue;
      const entry = groups.get(key) ?? {
        anatomyLabel: extractXrAnatomyWithoutLaterality(exam),
        LEFT: null,
        RIGHT: null,
        BILATERAL: null,
        NONE: null,
      };
      const lat = detectLaterality(`${exam.display_name} ${exam.body_region ?? ""} ${exam.exam_family_id}`);
      if (!entry[lat]) entry[lat] = exam;
      groups.set(key, entry);
    }
    return Array.from(groups.values()).sort((a, b) => a.anatomyLabel.localeCompare(b.anatomyLabel));
  }, [filteredExamFamilies]);

  const renderedSectionLines = useMemo(() => {
    return orderedSections
      .map((section) => {
        const style = config.assertion_styles[section.section_token] ?? "negative";
        const custom = (config.custom_assertions[section.section_token] ?? "").trim();
        const sectionPhraseOptions = getSectionRelevantPhraseOptions(
          section.section_token,
          section.section_title,
          style,
          assertionOptionsBySection
        );
        const phrase = config.phrase_variants[section.section_token] ?? sectionPhraseOptions[0] ?? PHRASE_OPTIONS_BY_STYLE[style][0];

        if (isOtherToken(section.section_token) && config.other_mode === "dynamic" && !custom) {
          return null;
        }

        const structuredLines = (config.section_structure_assertions[section.section_token] ?? [])
          .map((row) => row.text?.trim())
          .filter(Boolean);

        const fallbackLine = custom || stylePreview(section.section_title, style, phrase);
        const line = structuredLines.length > 0 ? structuredLines.join(" ") : fallbackLine;
        if (!line.trim()) return null;
        return { title: section.section_title.toUpperCase(), text: line };
      })
      .filter(Boolean) as Array<{ title: string; text: string }>;
  }, [orderedSections, config, assertionOptionsBySection]);

  const headerPreviewRows = useMemo(() => {
    const map: Record<string, string> = {
      EXAM: config.header.exam_label || selectedExam?.display_name || "",
      TECHNIQUE: config.header.technique || "Not specified.",
      COMPARISON: config.header.comparison || "None available.",
      INDICATION: config.header.standard_indication || "Compile-time indication",
    };

    const rows = config.header.field_order
      .filter((key) => map[key] !== undefined)
      .map((key) => ({ label: key, value: map[key] }));

    if (config.header.include_date) rows.push({ label: "DATE", value: "{{today_date}}" });
    if (config.header.include_contrast) rows.push({ label: "CONTRAST", value: selectedExam?.contrast_phase || "UNSPECIFIED" });
    if (config.header.include_age) rows.push({ label: "AGE", value: "{{patient_age}}" });
    if (config.header.include_dob) rows.push({ label: "DOB", value: "{{patient_dob}}" });

    return rows;
  }, [config.header, selectedExam]);

  const loadExamFamilies = useCallback(async () => {
    const { data, error: qErr } = await supabase
      .from("exam_family_registry")
      .select("exam_family_id, display_name, modality, body_region, contrast_phase")
      .eq("is_active", true)
      .order("display_name", { ascending: true });

    if (qErr) {
      setError(qErr.message);
      return;
    }

    const rows = (data ?? []) as ExamFamilyOption[];
    setExamFamilies(rows);
    if (!examFamilyId && rows[0]) setExamFamilyId(rows[0].exam_family_id);
  }, [examFamilyId]);

  const loadSectionsAndProfile = useCallback(async () => {
    if (!examFamilyId) return;
    setLoading(true);
    setError(null);
    setSaveMessage(null);

    const { data: sectionRows, error: sectionErr } = await supabase
      .from("template_section_registry")
      .select("section_token, section_title, order_index")
      .eq("exam_family_id", examFamilyId)
      .eq("is_active", true)
      .order("order_index", { ascending: true });

    if (sectionErr) {
      setLoading(false);
      setError(sectionErr.message);
      return;
    }

    const nextSections = ((sectionRows ?? []) as BaseSection[]).filter(
      (s) => s.section_token.toUpperCase() !== "IMPRESSION"
    );
    setSections(nextSections);

    const { data: structureRows } = await supabase
      .from("section_structure_map")
      .select("section_token, structure_key")
      .eq("exam_family_id", examFamilyId);

    const nextStructureMap: Record<string, string[]> = {};
    for (const row of (structureRows ?? []) as Array<{ section_token: string; structure_key: string }>) {
      const key = row.section_token;
      if (!nextStructureMap[key]) nextStructureMap[key] = [];
      if (!nextStructureMap[key].includes(row.structure_key)) nextStructureMap[key].push(row.structure_key);
    }
    Object.keys(nextStructureMap).forEach((k) => nextStructureMap[k].sort());
    setSectionStructureMap(nextStructureMap);

    const allStructureKeys = Array.from(new Set(Object.values(nextStructureMap).flat()));
    const modality = selectedExam?.modality ?? null;
    const buildOptionMap = (
      rows: AssertionRow[],
      field: "normal_text" | "negative_text"
    ): Record<string, string[]> => {
      const byStructure = new Map<string, string[]>();
      for (const r of rows) {
        const text = String(r[field] ?? "").trim();
        if (!text) continue;
        const list = byStructure.get(r.structure_key) ?? [];
        if (!list.includes(text)) list.push(text);
        byStructure.set(r.structure_key, list);
      }
      const bySection: Record<string, string[]> = {};
      for (const [sectionToken, structureKeys] of Object.entries(nextStructureMap)) {
        const set = new Set<string>();
        for (const sk of structureKeys) {
          for (const t of byStructure.get(sk) ?? []) set.add(t);
        }
        bySection[sectionToken] = Array.from(set).slice(0, 10);
      }
      return bySection;
    };

    let normalBySection: Record<string, string[]> = {};
    let negativeBySection: Record<string, string[]> = {};
    if (allStructureKeys.length > 0) {
      const normalQuery = untypedSupabase
        .from("normal_assertion_library")
        .select("structure_key, modality, normal_text")
        .in("structure_key", allStructureKeys)
        .eq("is_active", true);
      const negativeQuery = untypedSupabase
        .from("negative_assertion_library")
        .select("structure_key, modality, negative_text")
        .in("structure_key", allStructureKeys)
        .eq("is_active", true);

      const [normalRes, negativeRes] = await Promise.all([normalQuery, negativeQuery]);
      const normalRows = ((normalRes.data ?? []) as AssertionRow[]).filter(
        (r) => !r.modality || !modality || r.modality === modality
      );
      const negativeRows = ((negativeRes.data ?? []) as AssertionRow[]).filter(
        (r) => !r.modality || !modality || r.modality === modality
      );

      const normalByStructureMap = new Map<string, string[]>();
      for (const row of normalRows) {
        const text = String(row.normal_text ?? "").trim();
        if (!text) continue;
        const arr = normalByStructureMap.get(row.structure_key) ?? [];
        if (!arr.includes(text)) arr.push(text);
        normalByStructureMap.set(row.structure_key, arr);
      }
      const negativeByStructureMap = new Map<string, string[]>();
      for (const row of negativeRows) {
        const text = String(row.negative_text ?? "").trim();
        if (!text) continue;
        const arr = negativeByStructureMap.get(row.structure_key) ?? [];
        if (!arr.includes(text)) arr.push(text);
        negativeByStructureMap.set(row.structure_key, arr);
      }
      setNormalOptionsByStructure(Object.fromEntries(normalByStructureMap.entries()));
      setNegativeOptionsByStructure(Object.fromEntries(negativeByStructureMap.entries()));

      normalBySection = buildOptionMap(normalRows, "normal_text");
      negativeBySection = buildOptionMap(negativeRows, "negative_text");
    } else {
      setNormalOptionsByStructure({});
      setNegativeOptionsByStructure({});
    }

    const sectionOptionMap: Record<string, Record<AssertionStyle, string[]>> = {};
    for (const s of nextSections) {
      sectionOptionMap[s.section_token] = {
        normal: normalBySection[s.section_token] ?? [],
        negative: negativeBySection[s.section_token] ?? [],
        unremarkable: [`${s.section_title} is unremarkable.`],
      };
    }
    setAssertionOptionsBySection(sectionOptionMap);

    const {
      data: { user },
      error: userErr,
    } = await supabase.auth.getUser();

    if (userErr) {
      setLoading(false);
      setError(userErr.message);
      return;
    }

    if (!user) {
      setLoading(false);
      setConfig(defaultConfig(nextSections, selectedExam));
      return;
    }

    const { data: profileRows, error: profileErr } = await untypedSupabase
      .from("user_template_profiles")
      .select("id, profile_name, is_default, status, config")
      .eq("user_id", user.id)
      .eq("exam_family_id", examFamilyId)
      .order("updated_at", { ascending: false });

    if (profileErr) {
      if (/does not exist|schema cache/i.test(profileErr.message ?? "")) {
        const localKey = `rl:portal:template-profile:${examFamilyId}`;
        const raw = localStorage.getItem(localKey);
        if (raw) {
          try {
            const parsed = JSON.parse(raw) as Partial<TemplateProfileConfig>;
            setProfileId(null);
            setProfileName("Default");
            setIsDefault(true);
            setConfig(hydrateConfig(parsed, nextSections, selectedExam));
            setLoading(false);
            return;
          } catch {
            // fall through to default config
          }
        }
        setProfileId(null);
        setProfileName("Default");
        setIsDefault(true);
        setConfig(defaultConfig(nextSections, selectedExam));
        setLoading(false);
        return;
      }
      setLoading(false);
      setConfig(defaultConfig(nextSections, selectedExam));
      return;
    }

    const rows = (profileRows ?? []) as TemplateProfile[];
    const active = rows.find((r) => r.is_default) ?? rows[0];

    if (!active) {
      setProfileId(null);
      setProfileName("Default");
      setIsDefault(true);
      setConfig(defaultConfig(nextSections, selectedExam));
      setLoading(false);
      return;
    }

    setProfileId(active.id);
    setProfileName(active.profile_name || "Default");
    setIsDefault(!!active.is_default);
    setConfig(hydrateConfig(active.config, nextSections, selectedExam));
    setLoading(false);
  }, [examFamilyId, selectedExam]);

  useEffect(() => {
    loadExamFamilies();
  }, [loadExamFamilies]);

  useEffect(() => {
    loadSectionsAndProfile();
  }, [loadSectionsAndProfile]);

  const moveSection = (index: number, dir: -1 | 1) => {
    setConfig((prev) => {
      const arr = [...orderedSections.map((s) => s.section_token)];
      const target = index + dir;
      if (target < 0 || target >= arr.length) return prev;
      const tmp = arr[index];
      arr[index] = arr[target];
      arr[target] = tmp;
      return { ...prev, section_order: arr };
    });
  };

  const setStyle = (sectionToken: string, sectionTitle: string, style: AssertionStyle) => {
    const sectionOptions = getSectionRelevantPhraseOptions(
      sectionToken,
      sectionTitle,
      style,
      assertionOptionsBySection
    );
    setConfig((prev) => ({
      ...prev,
      assertion_styles: { ...prev.assertion_styles, [sectionToken]: style },
      phrase_variants: {
        ...prev.phrase_variants,
        [sectionToken]: prev.phrase_variants[sectionToken] ?? sectionOptions[0],
      },
    }));
  };

  const removeSectionFromProfile = (sectionToken: string) => {
    setConfig((prev) => ({
      ...prev,
      hidden_sections: prev.hidden_sections.includes(sectionToken)
        ? prev.hidden_sections
        : [...prev.hidden_sections, sectionToken],
    }));
  };

  const restoreSectionToProfile = (sectionToken: string) => {
    setConfig((prev) => ({
      ...prev,
      hidden_sections: prev.hidden_sections.filter((t) => t !== sectionToken),
    }));
  };

  const setPhrase = (sectionToken: string, phrase: string) => {
    setConfig((prev) => ({
      ...prev,
      phrase_variants: { ...prev.phrase_variants, [sectionToken]: phrase },
    }));
  };

  const setCustomAssertion = (sectionToken: string, text: string) => {
    setConfig((prev) => ({
      ...prev,
      custom_assertions: { ...prev.custom_assertions, [sectionToken]: text },
    }));
  };

  const setHeaderField = (
    field: "exam_label" | "technique" | "comparison" | "indication",
    value: string
  ) => {
    setConfig((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        [field]: value,
      },
    }));
  };

  const setXrDefaultViews = (value: number) => {
    const views = Math.max(1, Math.min(8, Math.round(value)));
    setConfig((prev) => {
      const next = {
        ...prev,
        header: {
          ...prev.header,
          xr_default_views: views,
          technique: xrTechniqueWithAnatomy(selectedExam, views),
        },
      };
      if (selectedExam?.modality === "XR") {
        next.header.exam_label = canonicalExamLabel(selectedExam, views);
      }
      return next;
    });
  };

  const moveHeaderField = (index: number, dir: -1 | 1) => {
    setConfig((prev) => {
      const arr = [...prev.header.field_order];
      const target = index + dir;
      if (target < 0 || target >= arr.length) return prev;
      const tmp = arr[index];
      arr[index] = arr[target];
      arr[target] = tmp;
      return {
        ...prev,
        header: {
          ...prev.header,
          field_order: arr,
        },
      };
    });
  };

  const getStructureStyleOptions = (structureKey: string, style: AssertionStyle): string[] => {
    if (style === "normal") return normalOptionsByStructure[structureKey] ?? [];
    if (style === "negative") return negativeOptionsByStructure[structureKey] ?? [];
    const label = structureKey.replace(/[_-]+/g, " ").trim();
    return [`${label.charAt(0).toUpperCase()}${label.slice(1)} is unremarkable.`];
  };

  const addStructureAssertion = (sectionToken: string, structureKey: string) => {
    if (!structureKey) return;
    setConfig((prev) => {
      const current = prev.section_structure_assertions[sectionToken] ?? [];
      if (current.some((c) => c.structure_key === structureKey)) return prev;
      const style: AssertionStyle = "negative";
      const options = getStructureStyleOptions(structureKey, style);
      const row: StructureAssertionSelection = {
        structure_key: structureKey,
        style,
        text: options[0] ?? "No acute abnormality.",
      };
      return {
        ...prev,
        section_structure_assertions: {
          ...prev.section_structure_assertions,
          [sectionToken]: [...current, row],
        },
      };
    });
  };

  const updateStructureAssertion = (
    sectionToken: string,
    structureKey: string,
    patch: Partial<StructureAssertionSelection>
  ) => {
    setConfig((prev) => ({
      ...prev,
      section_structure_assertions: {
        ...prev.section_structure_assertions,
        [sectionToken]: (prev.section_structure_assertions[sectionToken] ?? []).map((row) =>
          row.structure_key === structureKey ? { ...row, ...patch } : row
        ),
      },
    }));
  };

  const removeStructureAssertion = (sectionToken: string, structureKey: string) => {
    setConfig((prev) => ({
      ...prev,
      section_structure_assertions: {
        ...prev.section_structure_assertions,
        [sectionToken]: (prev.section_structure_assertions[sectionToken] ?? []).filter(
          (row) => row.structure_key !== structureKey
        ),
      },
    }));
  };

  const saveProfile = async (status: "draft" | "published") => {
    if (!examFamilyId) return;

    const persistConfig: TemplateProfileConfig = {
      section_order: orderedSections.map((s) => s.section_token),
      hidden_sections: [...config.hidden_sections],
      assertion_styles: Object.fromEntries(
        sections.map((s) => [s.section_token, config.assertion_styles[s.section_token] ?? "negative"])
      ),
      phrase_variants: Object.fromEntries(
        sections.map((s) => [
          s.section_token,
          config.phrase_variants[s.section_token] ?? PHRASE_OPTIONS_BY_STYLE[config.assertion_styles[s.section_token] ?? "negative"][0],
        ])
      ),
      custom_assertions: { ...config.custom_assertions },
      section_structure_assertions: { ...config.section_structure_assertions },
      other_mode: config.other_mode,
      header: {
        ...config.header,
        extra_header_lines: config.header.extra_header_lines.filter(Boolean),
      },
    };

    if (isAdmin && !allowAdminCloudSave) {
      const localKey = `rl:portal:template-profile:${examFamilyId}`;
      localStorage.setItem(localKey, JSON.stringify(persistConfig));
      setSaveMessage("Admin Safe Mode: saved locally only (DB unchanged)");
      return;
    }

    setLoading(true);
    setError(null);
    setSaveMessage(null);

    const {
      data: { user },
      error: userErr,
    } = await supabase.auth.getUser();

    if (userErr || !user) {
      setLoading(false);
      setError(userErr?.message ?? "Not signed in");
      return;
    }

    if (isDefault) {
      await untypedSupabase
        .from("user_template_profiles")
        .update({ is_default: false })
        .eq("user_id", user.id)
        .eq("exam_family_id", examFamilyId);
    }

    const payload = {
      user_id: user.id,
      exam_family_id: examFamilyId,
      profile_name: profileName.trim() || "Default",
      status,
      is_default: isDefault,
      config: persistConfig,
      updated_at: new Date().toISOString(),
    };

    const query = profileId
      ? untypedSupabase.from("user_template_profiles").update(payload).eq("id", profileId).select("id").single()
      : untypedSupabase.from("user_template_profiles").insert(payload).select("id").single();

    const { data, error: saveErr } = await query;
    setLoading(false);

    if (saveErr) {
      if (/does not exist|schema cache/i.test(saveErr.message ?? "")) {
        const localKey = `rl:portal:template-profile:${examFamilyId}`;
        localStorage.setItem(localKey, JSON.stringify(payload.config));
        setSaveMessage("Saved locally (profile table unavailable)");
        return;
      }
      setError(saveErr.message);
      return;
    }

    if (!profileId) setProfileId((data as { id?: string })?.id ?? null);
    setSaveMessage(status === "published" ? "Published template profile" : "Saved draft profile");
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Template Builder</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Find exams quickly, customize section-level phrasing, control OTHER behavior, edit report header fields, and preview a full report.
        </p>
      </div>

      <div className="flex flex-col gap-4">
      <div className="order-2 rounded-lg border border-border/50 p-3 bg-background/60 space-y-3">
        <div className="text-sm font-semibold">Exam Header Metadata and Order</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-muted-foreground">Exam Name</label>
            <Input value={config.header.exam_label} onChange={(e) => setHeaderField("exam_label", e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Technique</label>
            <Input value={config.header.technique} onChange={(e) => setHeaderField("technique", e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Comparison</label>
            <Input value={config.header.comparison} onChange={(e) => setHeaderField("comparison", e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Standard Indication (optional)</label>
            <select
              value={config.header.standard_indication ?? ""}
              onChange={(e) =>
                setConfig((prev) => ({
                  ...prev,
                  header: {
                    ...prev.header,
                    standard_indication: e.target.value || null,
                    indication: e.target.value || "Not specified.",
                  },
                }))
              }
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm"
            >
              <option value="">Use compile-time indication</option>
              {standardIndicationOptions(selectedExam).map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          {selectedExam?.modality === "XR" && (
            <div>
              <label className="text-xs text-muted-foreground">XR Default View Count</label>
              <div className="mt-1 flex items-center gap-2">
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => setXrDefaultViews((config.header.xr_default_views ?? 2) - 1)}
                >
                  -
                </Button>
                <Input
                  type="number"
                  min={1}
                  max={8}
                  value={config.header.xr_default_views ?? 2}
                  onChange={(e) => setXrDefaultViews(Number(e.target.value || 2))}
                  className="w-20 text-center"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => setXrDefaultViews((config.header.xr_default_views ?? 2) + 1)}
                >
                  +
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setConfig((prev) => ({
                    ...prev,
                    header: {
                      ...prev.header,
                      exam_label: canonicalExamLabel(selectedExam, prev.header.xr_default_views ?? 2),
                      technique: xrTechniqueWithAnatomy(selectedExam, prev.header.xr_default_views ?? 2),
                    },
                  }))}
                >
                  Apply XR Canonical
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                Canonical XR title format: <span className="font-mono">XR ANATOMY #V</span>
              </p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={config.header.include_date} onChange={(e) => setConfig((prev) => ({ ...prev, header: { ...prev.header, include_date: e.target.checked } }))} /> Include Date</label>
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={config.header.include_contrast} onChange={(e) => setConfig((prev) => ({ ...prev, header: { ...prev.header, include_contrast: e.target.checked } }))} /> Include Contrast</label>
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={config.header.include_age} onChange={(e) => setConfig((prev) => ({ ...prev, header: { ...prev.header, include_age: e.target.checked } }))} /> Include Age</label>
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={config.header.include_dob} onChange={(e) => setConfig((prev) => ({ ...prev, header: { ...prev.header, include_dob: e.target.checked } }))} /> Include DOB</label>
        </div>

        <div>
          <div className="text-xs text-muted-foreground mb-1">Header Field Order</div>
          <div className="space-y-1">
            {config.header.field_order.map((field, idx) => (
              <div key={`hdr-${field}`} className="flex items-center justify-between rounded border border-border/50 px-2 py-1">
                <span className="text-xs font-medium">{field}</span>
                <div className="flex items-center gap-1">
                  <Button type="button" size="icon" variant="outline" onClick={() => moveHeaderField(idx, -1)}>
                    <ArrowUp className="h-3 w-3" />
                  </Button>
                  <Button type="button" size="icon" variant="outline" onClick={() => moveHeaderField(idx, 1)}>
                    <ArrowDown className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground">Additional Header Lines (one per line)</label>
          <textarea
            value={config.header.extra_header_lines.join("\n")}
            onChange={(e) => setConfig((prev) => ({
              ...prev,
              header: {
                ...prev.header,
                extra_header_lines: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean),
              },
            }))}
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm min-h-[76px]"
          />
        </div>
      </div>

      <div className="order-1 rounded-lg border border-border/50 p-3 bg-background/60 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground">Search Exam (fuzzy)</label>
            <div className="mt-1 relative">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
              <Input
                value={examSearch}
                onChange={(e) => setExamSearch(e.target.value)}
                placeholder="e.g. mri lumbar wo, cta pe, chest ct"
                className="pl-8"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Modality</label>
            <select
              value={modalityFilter}
              onChange={(e) => setModalityFilter(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm"
            >
              {modalityOptions.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Contrast</label>
            <select
              value={contrastFilter}
              onChange={(e) => setContrastFilter(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm"
            >
              {contrastOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div>
            <label className="text-xs text-muted-foreground">Body Region</label>
            <select
              value={bodyRegionFilter}
              onChange={(e) => setBodyRegionFilter(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm"
            >
              {bodyRegionOptions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          {modalityFilter === "XR" && (
            <div>
              <label className="text-xs text-muted-foreground">Laterality</label>
              <select
                value={xrLateralityFilter}
                onChange={(e) => setXrLateralityFilter(e.target.value as "ALL" | XrLaterality)}
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm"
              >
                <option value="ALL">ALL</option>
                <option value="LEFT">LEFT</option>
                <option value="RIGHT">RIGHT</option>
                <option value="BILATERAL">BILATERAL</option>
              </select>
            </div>
          )}

          <div>
            <label className="text-xs text-muted-foreground">Profile Name</label>
            <Input value={profileName} onChange={(e) => setProfileName(e.target.value)} className="mt-1" />
          </div>

          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
              Make default
            </label>
          </div>

          <div>
            <label className="text-xs text-muted-foreground">OTHER Behavior</label>
            <select
              value={config.other_mode}
              onChange={(e) => setConfig((prev) => ({ ...prev, other_mode: e.target.value as OtherBehavior }))}
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm"
            >
              <option value="dynamic">Dynamic only (insert as needed)</option>
              <option value="persistent">Persistent in template</option>
            </select>
          </div>
        </div>

        {modalityFilter === "XR" ? (
          <div className="rounded-md border border-border/50 max-h-56 overflow-y-auto p-2 space-y-2">
            {groupedXrExamFamilies.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">No XR exams match current filters.</div>
            ) : (
              groupedXrExamFamilies.map((group) => (
                <div key={group.anatomyLabel} className="rounded border border-border/50 p-2">
                  <div className="text-xs font-semibold mb-2">{group.anatomyLabel.toUpperCase()}</div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {(["LEFT", "RIGHT", "BILATERAL"] as XrLaterality[]).map((lat) => {
                      const exam = group[lat];
                      const selected = exam?.exam_family_id === examFamilyId;
                      return (
                        <button
                          key={`${group.anatomyLabel}-${lat}`}
                          type="button"
                          disabled={!exam}
                          onClick={() => exam && setExamFamilyId(exam.exam_family_id)}
                          className={`text-xs rounded border px-2 py-1.5 transition-colors ${
                            !exam
                              ? "border-border/30 text-muted-foreground/60 bg-muted/20 cursor-not-allowed"
                              : selected
                                ? "border-primary bg-primary/10"
                                : "border-border/60 hover:bg-accent/40"
                          }`}
                        >
                          {lat}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="rounded-md border border-border/50 max-h-48 overflow-y-auto">
            {filteredExamFamilies.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground">No exams match current filters.</div>
            ) : (
              filteredExamFamilies.map((exam) => {
                const selected = exam.exam_family_id === examFamilyId;
                return (
                  <button
                    key={exam.exam_family_id}
                    type="button"
                    onClick={() => setExamFamilyId(exam.exam_family_id)}
                    className={`w-full text-left px-3 py-2 border-b last:border-b-0 border-border/40 transition-colors ${
                      selected ? "bg-primary/10" : "hover:bg-accent/30"
                    }`}
                  >
                    <div className="text-sm font-medium">
                      {exam.modality === "XR"
                        ? canonicalExamLabel(exam, config.header.xr_default_views ?? 2)
                        : exam.display_name}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {exam.modality} · {exam.contrast_phase || "UNSPECIFIED"} · {exam.body_region || "UNSPECIFIED"}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}

      </div>
      </div>

      {isAdmin && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <div className="font-medium text-amber-700 dark:text-amber-300">Admin Safe Mode</div>
          <p className="text-xs text-amber-700/90 dark:text-amber-200 mt-1">
            DB writes are disabled by default for admin testing. Enable cloud save only when you intentionally want to persist changes.
          </p>
          <label className="mt-2 inline-flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={allowAdminCloudSave}
              onChange={(e) => setAllowAdminCloudSave(e.target.checked)}
            />
            Enable cloud saves for this session
          </label>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="space-y-2 rounded-lg border border-border/50 p-3 bg-background/60 max-h-[700px] overflow-y-auto">
          {orderedSections.map((section, index) => {
            const style = config.assertion_styles[section.section_token] ?? "negative";
            const sectionPhraseOptions = getSectionRelevantPhraseOptions(
              section.section_token,
              section.section_title,
              style,
              assertionOptionsBySection
            );
            const phrase = config.phrase_variants[section.section_token]
              ?? sectionPhraseOptions[0]
              ?? PHRASE_OPTIONS_BY_STYLE[style][0];
            const custom = config.custom_assertions[section.section_token] ?? "";
            const relatedStructureKeys = sectionStructureMap[section.section_token] ?? [];
            const sectionQuery = sectionStructureSearch[section.section_token] ?? "";
            const filteredSectionStructureKeys = relatedStructureKeys.filter((k) =>
              fuzzyMatch(sectionQuery, k)
            );
            const selectedStructureRows = config.section_structure_assertions[section.section_token] ?? [];
            const pending = pendingStructureAdd[section.section_token] ?? "";
            return (
              <div key={section.section_token} className="rounded-md border border-border/50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium">{section.section_title}</div>
                    <div className="text-[11px] text-muted-foreground">{section.section_token}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="outline" onClick={() => moveSection(index, -1)}>
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="outline" onClick={() => moveSection(index, 1)}>
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => removeSectionFromProfile(section.section_token)}>
                      Remove
                    </Button>
                  </div>
                </div>

                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <select
                    value={style}
                    onChange={(e) => setStyle(section.section_token, section.section_title, e.target.value as AssertionStyle)}
                    className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  >
                    {STYLE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>

                  <select
                    value={phrase}
                    onChange={(e) => setPhrase(section.section_token, e.target.value)}
                    className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  >
                    {!sectionPhraseOptions.includes(phrase) && <option value={phrase}>{phrase}</option>}
                    {sectionPhraseOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="mt-2">
                  <label className="text-[11px] text-muted-foreground">Custom Assertion (optional override)</label>
                  <Input
                    value={custom}
                    onChange={(e) => setCustomAssertion(section.section_token, e.target.value)}
                    placeholder="Type custom text for this section"
                    className="mt-1"
                  />
                </div>
                <div className="mt-2">
                  <div className="text-[11px] text-muted-foreground">Related Structure Keys</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {relatedStructureKeys.length === 0 ? (
                      <span className="text-[11px] text-muted-foreground">No mapped keys found.</span>
                    ) : (
                      relatedStructureKeys.slice(0, 8).map((k) => (
                        <span key={k} className="text-[10px] px-1.5 py-0.5 rounded border border-border/50 bg-background">
                          {k}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                <div className="mt-3 rounded-md border border-border/50 p-2">
                  <div className="text-[11px] font-semibold text-muted-foreground">Structure-Level Assertions</div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Input
                      value={sectionQuery}
                      onChange={(e) =>
                        setSectionStructureSearch((prev) => ({
                          ...prev,
                          [section.section_token]: e.target.value,
                        }))
                      }
                      placeholder="Search structure key in this section"
                    />
                    <select
                      value={pending}
                      onChange={(e) =>
                        setPendingStructureAdd((prev) => ({
                          ...prev,
                          [section.section_token]: e.target.value,
                        }))
                      }
                      className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                    >
                      <option value="">Select structure...</option>
                      {filteredSectionStructureKeys.map((sk) => (
                        <option key={`${section.section_token}-${sk}`} value={sk}>
                          {sk}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!pending) return;
                        addStructureAssertion(section.section_token, pending);
                        setPendingStructureAdd((prev) => ({ ...prev, [section.section_token]: "" }));
                      }}
                    >
                      Add Structure
                    </Button>
                  </div>

                  {selectedStructureRows.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {selectedStructureRows.map((row) => {
                        const structureOptions = getStructureStyleOptions(row.structure_key, row.style);
                        const currentText = row.text || structureOptions[0] || "";
                        return (
                          <div key={`${section.section_token}-${row.structure_key}`} className="rounded border border-border/50 p-2">
                            <div className="text-[11px] font-medium mb-1">{row.structure_key}</div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              <select
                                value={row.style}
                                onChange={(e) => {
                                  const nextStyle = e.target.value as AssertionStyle;
                                  const opts = getStructureStyleOptions(row.structure_key, nextStyle);
                                  updateStructureAssertion(section.section_token, row.structure_key, {
                                    style: nextStyle,
                                    text: opts[0] ?? row.text,
                                  });
                                }}
                                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                              >
                                {STYLE_OPTIONS.map((opt) => (
                                  <option key={`${row.structure_key}-${opt}`} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={currentText}
                                onChange={(e) =>
                                  updateStructureAssertion(section.section_token, row.structure_key, {
                                    text: e.target.value,
                                  })
                                }
                                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                              >
                                {!structureOptions.includes(currentText) && (
                                  <option value={currentText}>{currentText}</option>
                                )}
                                {structureOptions.map((opt) => (
                                  <option key={`${row.structure_key}-${opt}`} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => removeStructureAssertion(section.section_token, row.structure_key)}
                              >
                                Remove
                              </Button>
                            </div>
                            <Input
                              className="mt-2"
                              value={currentText}
                              onChange={(e) =>
                                updateStructureAssertion(section.section_token, row.structure_key, {
                                  text: e.target.value,
                                })
                              }
                              placeholder="Optional free-text override for this structure"
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {removedSections.length > 0 && (
            <div className="rounded-md border border-dashed border-border/60 p-3 mt-2">
              <div className="text-xs font-semibold text-muted-foreground mb-2">Removed Sections</div>
              <div className="flex flex-wrap gap-1.5">
                {removedSections.map((s) => (
                  <button
                    key={`restore-${s.section_token}`}
                    type="button"
                    onClick={() => restoreSectionToProfile(s.section_token)}
                    className="text-xs px-2 py-1 rounded border border-border/60 hover:bg-accent/40"
                  >
                    Restore {s.section_title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-border/50 p-3 bg-background/60 max-h-[700px] overflow-y-auto">
          <div className="text-sm font-semibold mb-2">Complete Report Preview</div>
          <div className="space-y-2 text-sm">
            {headerPreviewRows.map((row) => (
              <div key={`hdr-prev-${row.label}`}>
                <span className="font-semibold">{row.label}:</span> {row.value}
              </div>
            ))}
            {config.header.extra_header_lines.map((line, idx) => (
              <div key={`header-extra-${idx}`}>{line}</div>
            ))}

            <div className="pt-2 font-semibold">FINDINGS:</div>
            {renderedSectionLines.length === 0 ? (
              <div className="text-muted-foreground">No section assertions configured yet.</div>
            ) : (
              renderedSectionLines.map((line) => (
                <div key={`${line.title}-${line.text}`}>
                  <span className="font-semibold">{line.title}:</span> {line.text}
                </div>
              ))
            )}

            <div className="pt-2 font-semibold">IMPRESSION:</div>
            {renderedSectionLines.slice(0, 3).map((line, idx) => (
              <div key={`imp-${idx}`}>{idx + 1}. {line.text}</div>
            ))}
            {renderedSectionLines.length === 0 && <div className="text-muted-foreground">1. No acute abnormality.</div>}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => saveProfile("draft")} disabled={loading || !examFamilyId}>
          <Save className="h-4 w-4 mr-1" /> Save Draft
        </Button>
        <Button size="sm" variant="secondary" onClick={() => saveProfile("published")} disabled={loading || !examFamilyId}>
          <Upload className="h-4 w-4 mr-1" /> Publish
        </Button>
        {saveMessage && <span className="text-xs text-emerald-600">{saveMessage}</span>}
        {error && <span className="text-xs text-destructive">{error}</span>}
      </div>
    </div>
  );
}
